import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useRegister } from "../apiCalls/userApiCalls";
import AuthSplit from "../components/AuthSplit";
import { Button, Input, Select, Label, Spinner } from "../components/ui";
import { ArrowLeftIcon } from "../components/ui/icons";
import { getData } from "country-list";

// ISO alpha-2 codes - what react-country-flag and the rankings filter expect.
const countries = getData();

// Two paths, asked before anything is created. Someone already in the register
// must not get a second player document — that duplicate is exactly what the
// claim flow exists to prevent — so their branch creates a bare account and
// hands them to /claim.
export default function Register() {
  const [mode, setMode] = useState(null); // null | "new" | "existing"

  const nameInput = useRef();
  const emailInput = useRef();
  const passwordInput = useRef();
  const genderInput = useRef();
  const nationalityInput = useRef();

  const { mutate: registerUser, isLoading, isError, error } = useRegister();

  const handleSubmit = (event) => {
    event.preventDefault();

    if (mode === "existing") {
      registerUser({
        email: emailInput.current.value,
        password: passwordInput.current.value,
        claimExisting: true,
      });
      return;
    }

    registerUser({
      name: nameInput.current.value,
      email: emailInput.current.value,
      password: passwordInput.current.value,
      gender: genderInput.current.value,
      nationality: nationalityInput.current.value,
    });
  };

  if (!mode) {
    return (
      <AuthSplit
        title="Create your account"
        subtitle="Free to join. Your profile can be completed later."
      >
        <p className="mb-5 text-sm font-semibold text-navy">
          Have you played a ranked tournament anywhere in the world before?
        </p>

        <div className="flex flex-col gap-3">
          <button
            type="button"
            onClick={() => setMode("existing")}
            className="rounded-sm border border-border p-5 text-left transition-colors hover:border-primary hover:bg-primary-wash"
          >
            <span className="block font-semibold text-navy">
              Yes — I'm already in the rankings
            </span>
            <span className="mt-1 block text-sm text-muted">
              We'll help you find and claim your existing profile.
            </span>
          </button>

          <button
            type="button"
            onClick={() => setMode("new")}
            className="rounded-sm border border-border p-5 text-left transition-colors hover:border-primary hover:bg-primary-wash"
          >
            <span className="block font-semibold text-navy">
              No — I'm new here
            </span>
            <span className="mt-1 block text-sm text-muted">
              We'll create a new player profile for you.
            </span>
          </button>
        </div>

        <p className="mt-6 text-center text-sm text-muted">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-semibold text-primary hover:underline"
          >
            Sign in
          </Link>
        </p>
      </AuthSplit>
    );
  }

  return (
    <AuthSplit
      title={mode === "existing" ? "Create your login" : "Create your account"}
      subtitle={
        mode === "existing"
          ? "We'll create your login, then help you find and claim the profile you already have in the register."
          : "Free to join. Your profile can be completed later."
      }
    >
      <button
        type="button"
        onClick={() => setMode(null)}
        className="mb-5 inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary-hover"
      >
        <ArrowLeftIcon className="h-4 w-4" />
        Back
      </button>

      <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
        {mode === "new" && (
          <div>
            <Label htmlFor="register-name">Full Name</Label>
            <Input
              id="register-name"
              type="text"
              ref={nameInput}
              required
              placeholder="Enter your full name"
            />
          </div>
        )}

        <div>
          <Label htmlFor="register-email">Email Address</Label>
          <Input
            id="register-email"
            type="email"
            ref={emailInput}
            required
            placeholder="Enter your email address"
          />
        </div>

        <div>
          <Label htmlFor="register-password">Password</Label>
          <Input
            id="register-password"
            type="password"
            ref={passwordInput}
            required
            minLength={6}
            placeholder="Create a password"
          />
        </div>

        {mode === "new" && (
          <>
            <div>
              <Label htmlFor="register-gender">Gender</Label>
              <Select id="register-gender" ref={genderInput}>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </Select>
            </div>

            <div>
              <Label htmlFor="register-nationality">Country</Label>
              <Select id="register-nationality" ref={nationalityInput}>
                <option value="">Select your country</option>
                {countries.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </Select>
            </div>
          </>
        )}

        {isError && (
          <p className="text-sm font-medium text-danger">
            {error?.response?.data?.error || "Registration failed."}
          </p>
        )}

        <Button type="submit" size="lg" disabled={isLoading}>
          {isLoading ? (
            <Spinner size="sm" />
          ) : mode === "existing" ? (
            "Continue"
          ) : (
            "Create Account"
          )}
        </Button>

        <p className="text-center text-sm text-muted">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-semibold text-primary hover:underline"
          >
            Sign in
          </Link>
        </p>
      </form>
    </AuthSplit>
  );
}
